import React, { useContext, useMemo, useState } from 'react'
import { createUpdateIntentPayload, intent } from '../../../models/axiosModel';
import { useIntentStore } from '../../../store/intentStore';
import { Checkbox, Divider, Grid, TextField, Typography } from '@mui/material';
import globalStyles from "../../../assets/styles/global.module.scss";
import style from "../../../assets/styles/componentStyles/campaignContactStyles/campaignContactStyle.module.scss";
import intentStyles from "../../../assets/styles/componentStyles/intentStyles/intentStyle.module.scss"; 
import chatbotStyles from "../../../assets/styles/componentStyles/chatbot.module.scss";
import Required from '../../utility/required';
import GlobalButton from '../../utility/globalButton/button';
import { UserContext, UserContextPayload } from '../../../context/userContext';
import agent from '../../../services/api';
import { ColorPicker } from 'material-ui-color';
import { isValidCssColor } from '../../../utils/constants';
import { useGenericStore } from '../../../store/genericStore';
import { useShallow } from 'zustand/react/shallow';


interface CreateIntentPopupProps {
    selectedIntent: intent | null, 
    flagSubIntentPopup: boolean, 
    setSelectedIntent: React.Dispatch<React.SetStateAction<intent | null>>,
    findIntentById: (id: string, intents: intent[]) => intent | null,
} 

const CreateIntentPopup: React.FC<CreateIntentPopupProps> = (props) => {

    const { selectedIntent, flagSubIntentPopup, setSelectedIntent, findIntentById } = props;

    const { setFlagLoading, handleError } = useContext(UserContext) as UserContextPayload

    const setFlagCreateIntentPopup = useIntentStore((store) => store.setFlagCreateIntentPopup);
    const getAnalyticsIntentApiCall = useIntentStore((store) => store.getAnalyticsIntentApiCall);
    const setIntentDataList = useIntentStore((store) => store.setIntentDataList);
    const intentDataList = useIntentStore((store) => store.intentDataList);

    const { showSuccess } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess
    })))

    const [payload, setPayload] = useState<createUpdateIntentPayload>(selectedIntent ? flagSubIntentPopup ? {
        intent_id: null, 
        intent_name: "", 
        parent_intent_id: selectedIntent?.intent_id, 
        intent_color: "", 
        flag_remaining: false,
        flag_show: selectedIntent?.flag_show
    } 
    :  selectedIntent 
    : {
        intent_id: null, 
        intent_name: "", 
        parent_intent_id: null, 
        intent_color: "", 
        flag_remaining: false,
        flag_show: true
    });

    const parentIntentOfSelectedIntent: intent | null = useMemo(() => {
        if(selectedIntent){
            if(flagSubIntentPopup) return selectedIntent;
            return findIntentById(selectedIntent?.parent_intent_id ?? '', intentDataList ?? []);
        }
        return null;
    }, [selectedIntent]);

    const checkDisableSaveButton = () => {
        if(payload?.intent_name?.trim()?.length == 0 || !isValidCssColor(payload.intent_color)){
            return true;
        }
        return false;
    }

    const getAnalyticsIntentApi = async () => {
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
        const res = await getAnalyticsIntentApiCall();
        if(!(res.status)){
            handleError(res);
        }
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
    }

    const handleSaveIntent = async () => {
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader:  true }));

        const res = await agent.createUpdateAnalyticsIntent.post(payload);
        if(res.status){
            showSuccess(`Intent ${(selectedIntent && !flagSubIntentPopup) ? 'Edited' : 'Created'} successfully.`)
            await getAnalyticsIntentApi();
        } else {
            handleError(res);
        }
        setFlagCreateIntentPopup(false);
        setSelectedIntent(null);
        setFlagLoading((prev) => ({ ...prev, flagRoundLoader:  false }));
    }

    const getIntentPopupTitle = () => {
        if(selectedIntent){
            if(flagSubIntentPopup){
                return `Create Sub Intent for ${selectedIntent?.intent_name}`;
            } else {
                return `Edit Intent`
            }
        } else {
            return "Create Intent";
        }
    }

    const checkFlagRemainingDisable = () => { 
        if(!selectedIntent){
            return true;
        } else {
            if(!flagSubIntentPopup){
                return true;
            }
        }
        if(selectedIntent){
            for(let i = 0; i < selectedIntent?.child_intent?.length; ++i){
                const currentIntent = selectedIntent?.child_intent[i];
                if(currentIntent?.flag_remaining){
                    return true;
                }
            }
        }
        return false;
    }

    const checkFlagShowInDashboardDisable = () => {
        if(selectedIntent){
            if(flagSubIntentPopup){
                return !selectedIntent?.flag_show;
            } else {
                if(parentIntentOfSelectedIntent){
                    return !parentIntentOfSelectedIntent?.flag_show;
                }
                return false;
            }
        }
        return false;
    }

    return (
        <>
            <Grid container>
                <Grid item xs={12}>
                    <Grid container className={`${globalStyles.flexColumn} ${globalStyles.justifyCenter}`}>
                        <Grid item className={globalStyles.textCenter}>
                            <Typography className={style.popupTitleTypography}>{getIntentPopupTitle()}</Typography>
                        </Grid>

                        <Divider className={style.popupTitleDivider} /> 

                        <Grid item xs={12} className={globalStyles.fullwidth}>
                            <Grid container className={style.popupBodyGridContainer} gap={2}>
                                <Grid item xs={12}>
                                    <Grid container className={`${globalStyles.flexColumn}`} gap={1}>
                                        <Grid item>
                                            <Typography className={style.popupFieldTypography}>Intent Name <Required /></Typography>
                                        </Grid>
                                        <Grid>
                                            <TextField
                                                value={payload.intent_name}
                                                onChange={(e) => { 
                                                    setPayload((prev) => ({
                                                        ...prev, 
                                                        intent_name: e.target.value,
                                                    }))
                                                }}
                                                placeholder='Enter Intent Name'
                                                className={`${style.campaignContactPopupTextField} ${globalStyles.fullwidth}`}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                    <Grid container className={`${globalStyles.flexColumn}`} gap={1}>
                                        <Grid item>
                                            <Typography className={style.popupFieldTypography}>Intent Color <Required /></Typography>
                                        </Grid>
                                        <Grid className={intentStyles.intentColorPicker}>
                                            <ColorPicker
                                                // name='color'
                                                value={typeof payload.intent_color === 'string' ? payload.intent_color : ''}
                                                onChange={(event: any) => {
                                                    let colorValue = "";
                                                    if (event.error) {
                                                        colorValue = event.raw;
                                                    }
                                                    else if (event.css && event.css.backgroundColor) { 
                                                        colorValue = event.css.backgroundColor;
                                                    } else if (event.target && event.target.value) {
                                                        colorValue = event.target.value;
                                                    } else {
                                                        colorValue = event;
                                                    }

                                                    
                                                    setPayload((prev) => ({
                                                        ...prev,
                                                        intent_color: colorValue,
                                                    }))
                                                }}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid> 

                                <Grid item xs={12}>
                                    <Grid container alignItems={"center"} gap={1}>
                                        <Grid item className={intentStyles.checkboxGridItem}>
                                            <Checkbox 
                                                className={`${checkFlagRemainingDisable() ? intentStyles.disableFlagRemainingCheckbox : intentStyles.flagRemainingCheckbox}`}
                                                disableRipple 
                                                checked={payload.flag_remaining} 
                                                disabled={checkFlagRemainingDisable()}
                                                onChange={(event) => { 
                                                    setPayload((prev) => ({
                                                        ...prev, 
                                                        flag_remaining: event.target.checked,
                                                    }))
                                                }} 
                                            />
                                        </Grid>
                                        <Grid item className={style.popupFieldTypography}>Flag Remaining</Grid>
                                    </Grid>
                                </Grid>

                                <Grid item xs={12}>
                                    <Grid container alignItems={"center"} gap={1}>
                                        <Grid item className={intentStyles.checkboxGridItem}>
                                            <Checkbox 
                                                className={`${checkFlagShowInDashboardDisable() ? intentStyles.disableFlagRemainingCheckbox : intentStyles.flagRemainingCheckbox}`}
                                                disableRipple 
                                                checked={payload.flag_show} 
                                                disabled={checkFlagShowInDashboardDisable()}
                                                onChange={(event) => { 
                                                    setPayload((prev) => ({
                                                        ...prev, 
                                                        flag_show: event.target.checked,
                                                    }))
                                                }} 
                                            />
                                        </Grid>
                                        <Grid item className={style.popupFieldTypography}>Display in Dashboard Insights</Grid>
                                    </Grid>
                                </Grid>

                            </Grid>
                        </Grid>

                        <Grid item xs={12} className={globalStyles.fullwidth}>
                            <Grid container justifyContent={"center"} alignItems={"center"} gap={"12px"} sx={{ padding: "20px 0" }}>
                                <Grid item>
                                    <GlobalButton
                                        buttonText="Cancel"
                                        sx={{ width: "130px", padding: "6.5px 20px !important", fontWeight: "500 !important" }}
                                        className='borderedButtonStyle'
                                        onClick={() => { setFlagCreateIntentPopup(false); setSelectedIntent(null);  }}
                                    />
                                </Grid>
                                <Grid item>
                                    <GlobalButton
                                        buttonText={(selectedIntent && !flagSubIntentPopup) ? "Save" : "Create"}
                                        sx={{ width: "130px", padding: "6.5px 20px !important", fontWeight: "500 !important" }}
                                        className='primaryButtonStyle' 
                                        disabled={checkDisableSaveButton()}
                                        onClick={() => handleSaveIntent()}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    )
}

export default CreateIntentPopup
